import { Chart } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import { RecentAnswer } from "winslow-core";
import { convertUTCtoLocal, toMMDD } from "winslow-core";

ChartJS.register(...registerables);

type RatingsChartProps = {
  answers: RecentAnswer[];
  startDate: Date; // inclusive
  endDate: Date; // inclusive
};

const RATING_BUCKETS = ["Not Rated", "1", "2", "3", "4", "5"] as const;
const RATING_COLORS: Record<(typeof RATING_BUCKETS)[number], string> = {
  "Not Rated": "#9CA3AF",
  "1": "#EF4444",
  "2": "#F59E0B",
  "3": "#EAB308",
  "4": "#84CC16",
  "5": "#22C55E",
};

export const RatingsChart = ({
  answers,
  startDate,
  endDate,
}: RatingsChartProps) => {
  const calculateDaysBetweenDates = (date1: Date, date2: Date) => {
    const differenceMillis = date2.getTime() - date1.getTime();
    return Math.round(differenceMillis / (1000 * 60 * 60 * 24));
  };

  const getBucketIndexForDate = (forDate: Date): number => {
    return calculateDaysBetweenDates(startDate, forDate);
  };

  const labels = (() => {
    const rangeDays = calculateDaysBetweenDates(startDate, endDate);
    const l: string[] = [];
    for (let i = 0; i <= rangeDays; i++) {
      const d = new Date(startDate.getTime());
      d.setDate(startDate.getDate() + i);
      l.push(toMMDD(d));
    }
    return l;
  })();

  // Initialize buckets: dayIndex -> { ratingLabel -> count }
  const rangeDays = calculateDaysBetweenDates(startDate, endDate);
  const buckets: Array<Record<(typeof RATING_BUCKETS)[number], number>> =
    Array.from({ length: rangeDays + 1 }, () => ({
      "Not Rated": 0,
      "1": 0,
      "2": 0,
      "3": 0,
      "4": 0,
      "5": 0,
    }));

  // Aggregate
  answers.forEach((ra) => {
    const ratedAt = ra.message?.feedbackRating?.updatedAt || ra.createdAt;
    const localDate = convertUTCtoLocal(new Date(ratedAt));
    if (localDate < startDate || localDate > endDate) return;
    const dayIdx = getBucketIndexForDate(localDate);
    const rating = ra.message?.feedbackRating?.rating ?? null;
    const key: (typeof RATING_BUCKETS)[number] = rating
      ? (String(rating) as any)
      : "Not Rated";
    buckets[dayIdx][key] = (buckets[dayIdx][key] || 0) + 1;
  });

  const datasets = RATING_BUCKETS.map((key) => ({
    label: key,
    data: buckets.map((b) => b[key] || 0),
    backgroundColor: RATING_COLORS[key],
    borderColor: RATING_COLORS[key],
    stack: "ratings",
  }));

  const data = { labels, datasets } as any;

  const options = {
    plugins: {
      title: { display: true, text: "Ratings (Last 30 Days)" },
      tooltip: { mode: "index" as const, intersect: false },
      legend: { display: true, position: "right" as const },
    },
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
        ticks: {
          autoSkip: false,
          maxRotation: 90 as const,
          minRotation: 90 as const,
        },
      },
      y: { stacked: true },
    },
  } as const;

  return <Chart type="bar" height="300px" data={data} options={options} />;
};

export default RatingsChart;
